/**
 * Easely Animator - Production Engine
 * v1.0.4 - GPU Optimized & Conflict Resistant
 */
import { PRESETS } from '../core/config';
import { registry } from '../core/registry';

export const animator = {
  /**
   * Safe access to Global GSAP (Critical for CDN/UMD users)
   */
  getGSAP() {
    return typeof window !== 'undefined' ? (window.gsap || null) : null;
  },

  /**
   * Triggers standalone animations with immediate execution
   */
  animate(el, settings) {
    const gsap = this.getGSAP();
    if (!el || !gsap) return;

    try {
      const config = this._prepareConfig(el, settings);
      
      // Standard standalone animation
      const tween = gsap.from(el, {
        ...config,
        // Ensure the element doesn't "flash" before the animation starts
        immediateRender: false 
      });
      
      registry.register(el, tween);
    } catch (error) {
      console.error(`Easely Animator: Failed to execute animation on`, el, error);
    }
  },

  /**
   * Generates a Tween object for Timeline orchestration
   */
  createTween(el, settings) {
    const gsap = this.getGSAP();
    if (!el || !gsap) return null;

    try {
      const config = this._prepareConfig(el, settings);
      // Timelines handle their own rendering logic
      return gsap.from(el, config);
    } catch (error) {
      console.warn(`Easely: Could not create tween for timeline child.`, error);
      return null;
    }
  },

  /**
   * Internal Config Factory: Merges Presets, Settings, and GPU Optimization
   */
  _prepareConfig(el, settings) {
    const preset = PRESETS[settings.type] || PRESETS['fade'];
    
    // GPU Preparation: Optimize layer for compositing
    el.style.willChange = 'transform, opacity';

    return {
      // 1. Inherit from Preset
      ...preset,
      
      // 2. Apply Core Settings
      duration: settings.duration,
      delay: settings.delay,
      ease: settings.ease,
      
      // 3. Loop Logic
      repeat: settings.repeat || 0,
      yoyo: settings.yoyo || false,
      
      // 4. Performance & Reliability
      force3D: true,           // Force GPU acceleration
      overwrite: 'auto',       // Kill conflicting animations on the same element
      lazy: true,              // Delay rendering for better performance frame-by-frame
      
      // 5. Custom Attribute Overrides (data-ez-to="opacity:0.5;scale:2")
      ...settings.customTo,
      
      // 6. Cleanup
      onComplete: () => {
        // Return memory to the browser once motion stops
        el.style.willChange = 'auto';
        if (settings.customTo?.onComplete) settings.customTo.onComplete();
      },
      
      // Safety: ensure it doesn't get stuck if overwritten
      onInterrupt: () => {
        el.style.willChange = 'auto';
      }
    };
  }
};